import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle2, AlertCircle } from "lucide-react";
import { apiUrl } from "@/lib/api-client";
import { type GpsUploadSource } from "@/lib/gallery-upload-gps";
import { uploadDealFolderPhotosConcurrent } from "@/lib/deal-folder-photos-concurrent-upload";
import { notify } from "@/lib/notify";
import { cn } from "@/lib/utils";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MultiPhotoCapture } from "@/components/images/MultiPhotoCapture";

interface PhotoCheckStepProps {
  dealId: string;
  /** Ha megadva, egy termékhez tartozó fotó lépés (pl. "Garázskapu 1") – a címsorban megjelenik */
  productLabel?: string;
  onPhotosUploaded?: () => void;
}

export function PhotoCheckStep({ dealId, productLabel, onPhotosUploaded }: PhotoCheckStepProps) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [uploadBusy, setUploadBusy] = useState(false);
  const [activeFolderId, setActiveFolderId] = useState<string | undefined>();

  const foldersQuery = useQuery({
    queryKey: ["image-folders", { dealId }],
    queryFn: async () => {
      if (!dealId) return [];
      const r = await fetch(apiUrl(`/api/images/folders?dealId=${dealId}`), {
        credentials: "include",
      });
      if (!r.ok) return [];
      const j = await r.json();
      return j.folders as { id: string }[];
    },
    enabled: !!dealId,
  });

  const folderIdFromList = foldersQuery.data?.[0]?.id as string | undefined;

  useEffect(() => {
    if (folderIdFromList) {
      setActiveFolderId(folderIdFromList);
    }
  }, [folderIdFromList]);

  const folderId = activeFolderId ?? folderIdFromList;

  const photosQuery = useQuery({
    queryKey: ["image-folder-photos", folderId, "bemeres", productLabel ?? ""],
    queryFn: async () => {
      if (!folderId) return [];
      const params = new URLSearchParams({ subfolderType: "bemeres" });
      if (productLabel?.trim()) params.set("productSlug", productLabel.trim());
      const r = await fetch(
        apiUrl(`/api/images/folders/${folderId}/photos?${params.toString()}`),
        { credentials: "include" },
      );
      if (!r.ok) return [];
      const j = await r.json();
      return j.photos as { id: string; thumbnail_url?: string; file_url?: string; file_name?: string }[];
    },
    enabled: !!folderId,
  });

  const photos = photosQuery.data || [];
  const hasPhotos = photos.length > 0;
  const title = useMemo(() => {
    const photo = t("measurementExtra.shortPhoto");
    if (productLabel?.trim()) {
      return t("measurementExtra.photoCheckTitleWithProduct", { product: productLabel.trim(), photo });
    }
    return t("measurementExtra.photoCheckTitleDefault");
  }, [productLabel, t, i18n.language]);

  const description = useMemo(() => {
    const count = photos.length;
    if (productLabel?.trim()) {
      const p = productLabel.trim();
      return hasPhotos
        ? t("measurementExtra.photoCheckDescProductHas", { count, product: p })
        : t("measurementExtra.photoCheckDescProductNeed", { product: p });
    }
    return hasPhotos
      ? t("measurementExtra.photoCheckDescDefaultHas", { count })
      : t("measurementExtra.photoCheckDescDefaultNeed");
  }, [productLabel, hasPhotos, photos.length, t, i18n.language]);

  const handleUpload = async (files: File[], sources: GpsUploadSource[]) => {
    if (!files.length || !dealId) return;
    setUploadBusy(true);
    try {
      let targetFolderId = folderId;
      if (!targetFolderId) {
        let dealTitle = "";
        try {
          const dealRes = await fetch(apiUrl(`/api/crm/deals/${dealId}`), { credentials: "include" });
          if (dealRes.ok) {
            const dealData = await dealRes.json();
            const d = dealData.deal ?? dealData;
            dealTitle = (d.title || d.name || "").trim() || t("measurementExtra.defaultDealTitle");
          }
        } catch {
          dealTitle = t("measurementExtra.defaultDealTitle");
        }
        const today = new Date();
        const dateStr = `${today.getFullYear()}.${String(today.getMonth() + 1).padStart(2, "0")}.${String(today.getDate()).padStart(2, "0")}`;
        const folderName = `${dateStr} ${dealTitle}`;

        const r = await fetch(apiUrl("/api/images/folders"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            name: folderName,
            dealId,
            category: "bemeres",
          }),
        });
        if (!r.ok) {
          const error = await r.json().catch(() => ({}));
          throw new Error(error?.userMessage || t("measurement.createFolderFailed"));
        }
        const j = await r.json();
        targetFolderId = j.folder?.id;
        if (targetFolderId) {
          setActiveFolderId(targetFolderId);
          await qc.invalidateQueries({ queryKey: ["image-folders", { dealId }] });
        }
      }

      if (!targetFolderId) {
        throw new Error(t("measurement.createPhotoFolderFailed"));
      }

      const uploadUrl = apiUrl(`/api/images/folders/${targetFolderId}/photos`);
      const slug = productLabel?.trim() || undefined;

      const onEachSuccess = () => {
        qc.invalidateQueries({
          queryKey: ["image-folder-photos", targetFolderId, "bemeres", productLabel ?? ""],
        });
        onPhotosUploaded?.();
      };

      const { anySuccess, queued } = await uploadDealFolderPhotosConcurrent({
        uploadUrl,
        subfolderType: "bemeres",
        files,
        sources,
        productSlug: slug,
        context: { folderId: targetFolderId, dealId },
        onEachSuccess,
      });

      if (anySuccess) {
        qc.invalidateQueries({
          queryKey: ["image-folder-photos", targetFolderId, "bemeres", productLabel ?? ""],
        });
      }

      if (queued > 0 && !anySuccess) {
        notify.error(
          t("uploadOutbox.queuedAllFailed", { count: queued }),
          t("uploadOutbox.queuedDealPhotosDesc"),
        );
      } else if (queued > 0) {
        notify.info(
          t("uploadOutbox.partialSuccess", { queued, ok: files.length - queued }),
          t("uploadOutbox.queuedDealPhotosDesc"),
        );
      }

      onPhotosUploaded?.();

      if (files.length >= 2 && (anySuccess || queued > 0)) {
        notify.info(
          t("images.uploadBackgroundStartedTitle"),
          t("images.uploadBackgroundStartedDesc", { count: files.length }),
        );
      }
    } catch (error) {
      notify.error(error instanceof Error ? error.message : t("measurementExtra.uploadFailedShort"));
    } finally {
      setUploadBusy(false);
    }
  };

  return (
    <div className="space-y-4 p-4">
      <Card className={cn(hasPhotos ? "border-green-500" : "border-yellow-500")}>
        <CardHeader>
          <div className="flex items-center gap-2">
            {hasPhotos ? (
              <CheckCircle2 className="h-5 w-5 text-green-500" />
            ) : (
              <AlertCircle className="h-5 w-5 text-yellow-500" />
            )}
            <CardTitle>{title}</CardTitle>
          </div>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!hasPhotos && (
            <div className="rounded-lg bg-yellow-50 dark:bg-yellow-900/20 p-4 text-sm text-yellow-800 dark:text-yellow-200">
              {t("measurementExtra.photoCheckWarningBeforeFinish")}
            </div>
          )}

          <MultiPhotoCapture
            onUpload={handleUpload}
            disabled={!dealId}
            uploadBusy={uploadBusy}
            releasePendingImmediately
            cameraLabel={t("images.takePhotos")}
            chooseFilesLabel={t("images.chooseFromFiles")}
            addPhotoLabel={t("images.addPhoto")}
            uploadLabel={t("images.uploadAll")}
            uploadingLabel={t("images.uploading")}
          />

          {hasPhotos && (
            <div className="grid grid-cols-3 gap-2 mt-4">
              {photos.slice(0, 6).map((photo) => (
                <div
                  key={photo.id}
                  className="aspect-square rounded-lg overflow-hidden border bg-muted"
                >
                  <img
                    src={photo.thumbnail_url || photo.file_url}
                    alt={photo.file_name || t("measurementExtra.photoAlt")}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
              {photos.length > 6 && (
                <div className="aspect-square rounded-lg border bg-muted flex items-center justify-center text-sm text-muted-foreground">
                  {t("measurementExtra.photoCheckMorePhotos", { count: photos.length - 6 })}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
